import { EmployeeProfile, CreateEmployeeRequest } from "../types/hr";

const API_BASE_URL = '/api/v1/employees/by-user'; // Base URL for employee HR profiles

export async function getEmployeeByUserId(userId: number): Promise<EmployeeProfile | null> {
  const res = await fetch(`${API_BASE_URL}/${userId}`);
  if (res.status === 404) return null; // No profile found
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || `Failed to fetch employee profile for user ${userId}`);
  }
  const data = await res.json();
  // Convert snake_case to camelCase for consistency
  return {
    id: data.id,
    userId: data.user_id || data.userId,
    jobTitle: data.job_title || data.jobTitle,
    employmentStartDate: data.employment_start_date || data.employmentStartDate,
    status: data.status,
    emergencyContactName: data.emergency_contact_name || data.emergencyContactName,
    emergencyContactPhone: data.emergency_contact_phone || data.emergencyContactPhone,
    workSchedule: data.work_schedule || data.workSchedule,
    notes: data.notes,
    userEmail: data.user_email || data.userEmail,
    userFullName: data.user_full_name || data.userFullName,
    tenantId: data.tenant_id || data.tenantId,
    createdAt: data.created_at || data.createdAt,
    updatedAt: data.updated_at || data.updatedAt,
  };
}

export async function createEmployeeForUser(userId: number, data: CreateEmployeeRequest): Promise<EmployeeProfile> {
  const payload = {
    ...data,
    user_id: data.userId, // Ensure snake_case for backend
    employment_start_date: data.employmentStartDate,
    emergency_contact_name: data.emergencyContactName,
    emergency_contact_phone: data.emergencyContactPhone,
    work_schedule: data.workSchedule,
    job_title: data.jobTitle,
  };
  const res = await fetch(`${API_BASE_URL}/${userId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "Failed to create employee profile");
  }
  const responseData = await res.json();
  // Convert snake_case to camelCase for consistency
  return {
    id: responseData.id,
    userId: responseData.user_id || responseData.userId,
    jobTitle: responseData.job_title || responseData.jobTitle,
    employmentStartDate: responseData.employment_start_date || responseData.employmentStartDate,
    status: responseData.status,
    emergencyContactName: responseData.emergency_contact_name || responseData.emergencyContactName,
    emergencyContactPhone: responseData.emergency_contact_phone || responseData.emergencyContactPhone,
    workSchedule: responseData.work_schedule || responseData.workSchedule,
    notes: responseData.notes,
    userEmail: responseData.user_email || responseData.userEmail,
    userFullName: responseData.user_full_name || responseData.userFullName,
    tenantId: responseData.tenant_id || responseData.tenantId,
    createdAt: responseData.created_at || responseData.createdAt,
    updatedAt: responseData.updated_at || responseData.updatedAt,
  };
}

export async function updateEmployeeForUser(userId: number, data: CreateEmployeeRequest): Promise<EmployeeProfile> {
  const payload = {
    ...data,
    user_id: data.userId, // Ensure snake_case for backend
    employment_start_date: data.employmentStartDate,
    emergency_contact_name: data.emergencyContactName,
    emergency_contact_phone: data.emergencyContactPhone,
    work_schedule: data.workSchedule,
    job_title: data.jobTitle,
  };
  const res = await fetch(`${API_BASE_URL}/${userId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "Failed to update employee profile");
  }
  const responseData = await res.json();
  // Convert snake_case to camelCase for consistency
  return {
    id: responseData.id,
    userId: responseData.user_id || responseData.userId,
    jobTitle: responseData.job_title || responseData.jobTitle,
    employmentStartDate: responseData.employment_start_date || responseData.employmentStartDate,
    status: responseData.status,
    emergencyContactName: responseData.emergency_contact_name || responseData.emergencyContactName,
    emergencyContactPhone: responseData.emergency_contact_phone || responseData.emergencyContactPhone,
    workSchedule: responseData.work_schedule || responseData.workSchedule,
    notes: responseData.notes,
    userEmail: responseData.user_email || responseData.userEmail,
    userFullName: responseData.user_full_name || responseData.userFullName,
    tenantId: responseData.tenant_id || responseData.tenantId,
    createdAt: responseData.created_at || responseData.createdAt,
    updatedAt: responseData.updated_at || responseData.updatedAt,
  };
}

export async function deleteEmployeeForUser(userId: number): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/${userId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "Failed to delete employee profile");
  }
}

// For POST /by-user/{userId} endpoint
export async function createEmployeeProfileForUser(userId: number, data: CreateEmployeeRequest): Promise<EmployeeProfile> {
  const payload = {
    ...data,
    user_id: userId, // Ensure snake_case for backend
    employment_start_date: data.employmentStartDate,
    emergency_contact_name: data.emergencyContactName,
    emergency_contact_phone: data.emergencyContactPhone,
    work_schedule: data.workSchedule,
    job_title: data.jobTitle,
  };
  const res = await fetch(`${API_BASE_URL}/${userId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "Failed to create employee profile");
  }
  const responseData = await res.json();
  // Convert snake_case to camelCase for consistency
  return {
    id: responseData.id,
    userId: responseData.user_id || responseData.userId,
    jobTitle: responseData.job_title || responseData.jobTitle,
    employmentStartDate: responseData.employment_start_date || responseData.employmentStartDate,
    status: responseData.status,
    emergencyContactName: responseData.emergency_contact_name || responseData.emergencyContactName,
    emergencyContactPhone: responseData.emergency_contact_phone || responseData.emergencyContactPhone,
    workSchedule: responseData.work_schedule || responseData.workSchedule,
    notes: responseData.notes,
    userEmail: responseData.user_email || responseData.userEmail,
    userFullName: responseData.user_full_name || responseData.userFullName,
  };
}

export async function patchEmployeeProfile(userId: number, partial: Partial<EmployeeProfile>): Promise<EmployeeProfile> {
  // Convert camelCase to snake_case for backend
  const payload: Record<string, any> = {};
  
  if (partial.jobTitle !== undefined) payload.job_title = partial.jobTitle;
  if (partial.status !== undefined) payload.status = partial.status;
  if (partial.emergencyContactName !== undefined) payload.emergency_contact_name = partial.emergencyContactName;
  if (partial.emergencyContactPhone !== undefined) payload.emergency_contact_phone = partial.emergencyContactPhone;
  if (partial.workSchedule !== undefined) payload.work_schedule = partial.workSchedule;
  if (partial.notes !== undefined) payload.notes = partial.notes;
  
  const res = await fetch(`${API_BASE_URL}/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "Failed to update employee profile");
  }
  
  const responseData = await res.json();
  // Convert snake_case to camelCase for consistency
  return {
    id: responseData.id,
    userId: responseData.user_id || responseData.userId,
    jobTitle: responseData.job_title || responseData.jobTitle,
    employmentStartDate: responseData.employment_start_date || responseData.employmentStartDate,
    status: responseData.status,
    emergencyContactName: responseData.emergency_contact_name || responseData.emergencyContactName,
    emergencyContactPhone: responseData.emergency_contact_phone || responseData.emergencyContactPhone,
    workSchedule: responseData.work_schedule || responseData.workSchedule,
    notes: responseData.notes,
    userEmail: responseData.user_email || responseData.userEmail,
    userFullName: responseData.user_full_name || responseData.userFullName,
    tenantId: responseData.tenant_id || responseData.tenantId,
    createdAt: responseData.created_at || responseData.createdAt,
    updatedAt: responseData.updated_at || responseData.updatedAt,
  };
}